import type { ReactElement, ReactNode } from 'react';
import React, { useMemo } from 'react';

import type { INamedContainerProps } from '../models';

export interface INamedContainers {
    [key: string]: ReactNode;
}

interface INamedContainersWithChildren extends INamedContainers {
    children: ReactNode[];
}

export function isNamedContainer(node: ReactNode): node is ReactElement<INamedContainerProps> {
    return Boolean(
        typeof node === 'object' &&
            node !== null &&
            (node as ReactElement).props.is === 'NamedContainer' &&
            (node as ReactElement).props.name
    );
}

/**
 * Функция собирает из children слоты и оставляет отфильтрованный список детей
 * @param children
 */
export function getNamedContainersWithChildren(children: ReactNode) {
    return React.Children.toArray(children).reduce(
        (nodes: INamedContainersWithChildren, node) => {
            if (!isNamedContainer(node)) {
                nodes.children.push(node);
            } else if (node.props.name !== 'children') {
                return {
                    ...nodes,
                    [node.props.name]: node.props.children,
                };
            }

            return nodes;
        },
        { children: [] }
    );
}

/**
 * Функция собирает из children только слоты
 * @param children
 */
export function getNamedContainers(children: ReactNode) {
    return React.Children.toArray(children).reduce((nodes: INamedContainers, node) => {
        if (isNamedContainer(node)) {
            return {
                ...nodes,
                [node.props.name]: node.props.children,
            };
        }

        return nodes;
    }, {});
}

export function useNamedContainersWithChildren(children: ReactNode): INamedContainersWithChildren {
    return useMemo(() => getNamedContainersWithChildren(children), [children]);
}

export function useNamedContainers(children: ReactNode) {
    return useMemo(() => getNamedContainers(children), [children]);
}
